import http from '../../../services/api'
import type { APIPaginatedResponse } from '../../../services/types'

const apiPath = import.meta.env.VITE_API_PATH

/** One aggregated row from GET reports/items/sold. */
export type ItemsSoldReportRow = {
  item_uuid: string
  item_name: string
  code: string | null
  quantity_sold: number
  quantity_issued: number
  gross_sales: number
  discount_amount: number
  loyalty_amount: number
  net_sales: number
  transactions_count: number
}

/** One line-level row from GET reports/items/sold/lines. */
export type ItemsSoldLinesReportRow = {
  date_time: string
  client: string
  client_uuid: string | null
  sale_no: string
  sale_uuid: string
  invoice_no: string
  invoice_uuid: string
  item: string
  item_uuid: string
  quantity: number
  unit_price: number
  total_amount: number
  discount_amount: number
  loyalty_amount: number
  net_amount: number
  created_by: string
}

export type ItemsSoldReportParams = {
  start_date: string
  end_date: string
  /** Omit for active branch; empty string requests all branches (multi-branch access). */
  branch_id?: number | '' | null
  location_id?: number | null
  search?: string | null
  page?: number
  per_page?: number
}

function appendItemsSoldQueryParams(params: ItemsSoldReportParams): URLSearchParams {
  const queryParams = new URLSearchParams({
    start_date: params.start_date,
    end_date: params.end_date,
  })

  if (params.search?.trim()) {
    queryParams.set('search', params.search.trim())
  }

  if (params.location_id != null) {
    queryParams.set('location_id', String(params.location_id))
  }

  if (params.page != null) {
    queryParams.set('page', String(params.page))
  }

  if (params.per_page != null) {
    queryParams.set('per_page', String(params.per_page))
  }

  if (params.branch_id === '') {
    queryParams.set('branch_id', '')
  } else if (params.branch_id != null) {
    queryParams.set('branch_id', String(params.branch_id))
  }

  return queryParams
}

async function getItemsSoldReport(params: ItemsSoldReportParams) {
  const queryParams = appendItemsSoldQueryParams(params)

  return await http.get<APIPaginatedResponse<ItemsSoldReportRow>>(
    `${apiPath}reports/items/sold?${queryParams.toString()}`,
    { errorHandle: false, timeout: 30000 } as any,
  )
}

async function getItemsSoldLinesReport(params: ItemsSoldReportParams) {
  const queryParams = appendItemsSoldQueryParams(params)

  return await http.get<APIPaginatedResponse<ItemsSoldLinesReportRow>>(
    `${apiPath}reports/items/sold/lines?${queryParams.toString()}`,
    { errorHandle: false, timeout: 30000 } as any,
  )
}

export default {
  getItemsSoldReport,
  getItemsSoldLinesReport,
}
